import React, { useState, useContext, useCallback } from 'react';
import { Modal, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import { ExportHubAPI, ExportImportJob } from 'api/ExportHubAPI';
import {
  checkProductCredits,
  type ProductCreditCheck,
} from 'utils/productCredits';
import ProductCreditsModal from 'pages/import/ProductCreditsModal';
import { getUploadRowCount } from 'pages/import/CreateImportDatabaseDialog';
import * as XLSX from 'xlsx';

interface Props {
  open: boolean;
  databaseId: string;
  onClose: () => void;
  // Called after a successful import (≥1 row inserted) so the parent can refresh
  // the products table. The modal stays open to show the result summary.
  onImported: () => void;
}

type Phase = 'select' | 'uploading' | 'result';

// Friendly headers the server parser understands (see importParser HEADER_ALIASES),
// plus one example row. Offered as a downloadable starting point.
const TEMPLATE_HEADERS = [
  'Name',
  'Internal SKU',
  'Description',
  'Manufacturer',
  'Country of Origin',
  'Materials',
  'Intended End Use',
  'Hardware Authorized',
  'Tech Data Authorized',
  'Quantity',
  'Unit of Measure',
  'HTS Code',
  'ECCN',
  'Destination Countries',
  'Tier',
  'Jurisdiction',
];

const TEMPLATE_SAMPLE = [
  'Widget Assembly A1',
  'SKU-1001',
  'Precision machined aluminum housing',
  'Acme Mfg',
  'US',
  'Aluminum 6061',
  'Commercial avionics',
  'Y',
  'N',
  '100',
  'EA',
  '8803.30.0030',
  '7A994',
  'DE; FR; JP',
  'Component',
  'EAR',
];

// The server's importParser skips any row without a name (ImportProductRowSchema
// requires it), so a raw row count over-states what the upload will insert and
// can reject an import that actually fits. Resolve the name column the same way
// the parser does — normalise to lowercase alphanumerics, match its aliases —
// and count only rows that fill it.
const NAME_ALIASES = new Set(['name', 'productname']);

const normalizeHeader = (h: string): string =>
  h.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Index of the name column in the upload's header row, or null. */
const findNameColumn = async (file: File): Promise<number | null> => {
  try {
    let headers: string[] = [];
    if (/\.xlsx?$/i.test(file.name)) {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, {
        header: 1,
        blankrows: false,
        defval: '',
      });
      headers = (aoa[0] || []).map((h) => String(h ?? ''));
    } else {
      const text = await file.text();
      const firstLine = text.split(/\r?\n/)[0] || '';
      headers = firstLine.split(',').map((h) => h.replace(/^"|"$/g, ''));
    }
    const idx = headers.findIndex((h) => NAME_ALIASES.has(normalizeHeader(h)));
    return idx >= 0 ? idx : null;
  } catch {
    // Unreadable header — fall back to the raw count rather than blocking.
    return null;
  }
};

const csvCell = (v: string): string =>
  /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

const downloadTemplate = (): void => {
  const csv = `${TEMPLATE_HEADERS.map(csvCell).join(',')}\n${TEMPLATE_SAMPLE.map(
    csvCell,
  ).join(',')}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'export-hub-products-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ImportProductsModal: React.FC<Props> = ({
  open,
  databaseId,
  onClose,
  onImported,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;
  const logAppAction = useLogAppAction();

  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('select');
  const [job, setJob] = useState<ExportImportJob | null>(null);
  const [creditBlock, setCreditBlock] = useState<ProductCreditCheck | null>(
    null,
  );

  const reset = useCallback((): void => {
    setFile(null);
    setPhase('select');
    setJob(null);
  }, []);

  const handleClose = useCallback((): void => {
    if (phase === 'uploading') return; // don't close mid-upload
    reset();
    onClose();
  }, [phase, reset, onClose]);

  const onDrop = useCallback((accepted: FileWithPath[]): void => {
    // FileWithPath extends File; cast keeps the state typed as the DOM File.
    const f = accepted[0] as File | undefined;
    if (f) setFile(f);
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop: onDrop,
      accept: [
        'text/csv',
        '.csv',
        '.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ],
      multiple: false,
    });

  const handleSubmit = async (): Promise<void> => {
    if (!file || !groupId) {
      enqueueSnackbar('Select a file to import.', { variant: 'warning' });
      return;
    }
    logAppAction('export_hub:import_products');
    setPhase('uploading');
    try {
      // Same product-credit pool as the Import Hub. The server parses the file,
      // so count the rows here first — the check has to happen before the
      // upload, not after rows have already landed.
      const nameColIndex = await findNameColumn(file);
      const rowCount = await getUploadRowCount(
        file,
        nameColIndex === null ? undefined : { nameColIndex: nameColIndex },
      );
      const check = await checkProductCredits(groupId, rowCount);
      if (!check.ok) {
        setCreditBlock(check);
        setPhase('select');
        return;
      }
      const result = await ExportHubAPI.importProducts(databaseId, groupId, file);
      setJob(result);
      setPhase('result');
      if (result.rows_inserted > 0) {
        onImported();
        enqueueSnackbar(
          `Imported ${result.rows_inserted} product${
            result.rows_inserted === 1 ? '' : 's'
          }.`,
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar('No products were imported — check the row errors.', {
          variant: 'warning',
        });
      }
    } catch (e: any) {
      setPhase('select');
      enqueueSnackbar(
        e?.response?.data?.message ||
          (e instanceof Error ? e.message : 'Import failed.'),
        { variant: 'error' },
      );
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Import Products'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Import Products</h2>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          {phase === 'select' && (
            <>
              <p className='eh-modal-subtitle'>
                Upload a CSV or XLSX of products. Columns are matched by header
                name (e.g. &quot;HTS Code&quot;, &quot;ECCN&quot;,
                &quot;Jurisdiction&quot;). Rows with errors are skipped and
                reported; valid rows are imported.
              </p>

              <button
                className='eh-linkish'
                type='button'
                onClick={downloadTemplate}
                style={{ marginBottom: 12 }}
              >
                <DescriptionOutlinedIcon style={{ fontSize: 16 }} /> Download CSV
                template
              </button>

              <div
                {...getRootProps()}
                className={`eh-dropzone ${isDragActive ? 'active' : ''} ${
                  isDragReject ? 'reject' : ''
                } ${file ? 'has-file' : ''}`}
              >
                <input {...getInputProps()} />
                {file ? (
                  <div className='eh-dropzone-inner'>
                    <CheckCircleOutlineIcon
                      style={{ fontSize: 22, color: 'var(--full-bar-color)' }}
                    />
                    <span className='eh-dropzone-file'>{file.name}</span>
                    <span className='eh-dropzone-sub'>Click to replace</span>
                  </div>
                ) : (
                  <div className='eh-dropzone-inner'>
                    <span className='eh-dropzone-text'>
                      <strong>Click to upload</strong> or drag &amp; drop
                    </span>
                    <span className='eh-dropzone-sub'>CSV or XLSX</span>
                    {isDragReject && (
                      <span className='eh-dropzone-err'>
                        Unsupported file type.
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {phase === 'uploading' && (
            <div className='eh-import-busy'>
              <CircularProgress size={32} />
              <span className='eh-import-busy-text'>
                Importing products… this can take a moment for large files.
              </span>
            </div>
          )}

          {phase === 'result' && job && (
            <div className='eh-import-result'>
              <div className='eh-import-summary'>
                {job.rows_failed === 0 ? (
                  <CheckCircleOutlineIcon
                    style={{ fontSize: 28, color: 'var(--full-bar-color)' }}
                  />
                ) : (
                  <ErrorOutlineIcon
                    style={{ fontSize: 28, color: 'var(--mid-color)' }}
                  />
                )}
                <div>
                  <div className='eh-import-summary-title'>
                    Imported {job.rows_inserted} of {job.rows_total} row
                    {job.rows_total === 1 ? '' : 's'}
                  </div>
                  {job.rows_failed > 0 && (
                    <div className='eh-import-summary-sub'>
                      {job.rows_failed} row{job.rows_failed === 1 ? '' : 's'}{' '}
                      skipped due to errors.
                    </div>
                  )}
                </div>
              </div>

              {job.errors.length > 0 && (
                <div className='eh-import-errors'>
                  <div className='eh-import-errors-head'>
                    Row errors{' '}
                    {job.errors.length < job.rows_failed
                      ? `(showing first ${job.errors.length})`
                      : ''}
                  </div>
                  <div className='eh-import-errors-list'>
                    {job.errors.map((err) => (
                      <div className='eh-import-error-row' key={err.row}>
                        <span className='eh-import-error-rownum'>
                          Row {err.row}
                        </span>
                        <span className='eh-import-error-issues'>
                          {err.issues.join('; ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className='eh-modal-foot'>
          {phase === 'result' ? (
            <>
              <button className='btn-outline' type='button' onClick={reset}>
                Import another
              </button>
              <button className='btn-primary' type='button' onClick={handleClose}>
                Done
              </button>
            </>
          ) : (
            <>
              <button
                className='btn-outline'
                type='button'
                onClick={handleClose}
                disabled={phase === 'uploading'}
              >
                Cancel
              </button>
              <button
                className='btn-primary'
                type='button'
                onClick={handleSubmit}
                disabled={!file || phase === 'uploading'}
                style={{ opacity: !file || phase === 'uploading' ? 0.6 : 1 }}
              >
                {phase === 'uploading' ? 'Importing…' : 'Import'}
              </button>
            </>
          )}
        </div>
      </div>
      </Modal>
      <ProductCreditsModal
        check={creditBlock}
        teamId={groupId || ''}
        onClose={() => setCreditBlock(null)}
      />
    </>
  );
};

export default ImportProductsModal;
